/**
 * PATH: packages/email-hub/src/feature/components/rich-text/selectionHtml.ts
 * FILE_ID: EMAILHUB-RICHTEXT-SELECTIONHTML
 * PURPOSE: Serialise the current editor selection (or the whole body when the
 *          selection is empty) to an HTML fragment — used to capture a block
 *          for "Save as snippet".
 * FLOW: editor.selection → ProseMirror slice → DOMSerializer → innerHTML
 * DEPENDENCIES: @tiptap/core (Editor), @tiptap/pm/model (DOMSerializer)
 * RELATED: snippetStore.ts (persists the captured html), RichTextEditor.tsx.
 */
import type { Editor } from '@tiptap/core'
import { DOMSerializer } from '@tiptap/pm/model'

/**
 * HTML for the current selection, or the whole document when nothing is
 * selected. Returns '' if there's no editor.
 */
export function getSelectionOrBodyHtml(editor: Editor | null): string {
  if (!editor) return ''
  const { from, to, empty } = editor.state.selection
  if (empty) return editor.getHTML()
  return sliceHtml(editor, from, to)
}

/**
 * HTML for the current selection ONLY; empty string when nothing is selected
 * (unlike getSelectionOrBodyHtml, which falls back to the whole body). Used by
 * the free-form "Ask AI…" path so the model gets just the highlighted fragment
 * (e.g. a table to fill in), not the entire draft.
 */
export function getSelectionHtml(editor: Editor | null): string {
  if (!editor) return ''
  const { from, to, empty } = editor.state.selection
  if (empty) return ''
  return sliceHtml(editor, from, to)
}

function sliceHtml(editor: Editor, from: number, to: number): string {
  const slice = editor.state.doc.slice(from, to)
  const serializer = DOMSerializer.fromSchema(editor.schema)
  const fragment = serializer.serializeFragment(slice.content)
  const div = document.createElement('div')
  div.appendChild(fragment)
  return div.innerHTML
}
